import { useState, type FormEvent } from 'react'
import { Navigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { useSession } from '@/hooks/useSession'
import { PrincipalPicker } from '@/components/PrincipalPicker'
import { pt } from '@/i18n/pt-PT'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { FormField } from '@/components/ui/FormField'
import { MfaChallenge } from './MfaChallenge'
import { MfaEnroll } from './MfaEnroll'

// Passwordless sign-in: the ONLY method is a magic link (the same flow the old
// "forgot password" used). No password field — passwords are a weaker/riskier
// vector, need resets/recovery, and staff kept hitting the "can't set a password
// after a magic-link login" dead end. MFA (enroll/challenge) still applies after
// the link is followed.
export function Login() {
  const session = useSession()
  const [email, setEmail] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [magicLinkSentTo, setMagicLinkSentTo] = useState<string | null>(null)

  if (session.status === 'needs_principal_choice') {
    // Several principals and no remembered choice — ask rather than guessing a landing area.
    return <PrincipalPicker principals={session.principals} />
  }
  if (session.status === 'authenticated_client') {
    return <Navigate to="/portal/inicio" replace />
  }
  if (session.status === 'authenticated' || session.status === 'unprovisioned') {
    return <Navigate to="/operacoes" replace />
  }
  if (session.status === 'needs_mfa_enroll') {
    return <MfaEnroll onVerified={() => void 0} />
  }
  if (session.status === 'needs_mfa_challenge') {
    return <MfaChallenge factorId={session.factorId} onVerified={() => void 0} />
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    // shouldCreateUser: FALSE. Without it Supabase defaults to true, so this public form created an
    // auth account for ANY address typed into it — measured 2026-09-26: 10 accounts existed with no
    // staff row and no portal access, 5 of them signed in, and that is exactly the current_role()
    // NULL shape the !426 write-RPC exploit relied on. Accounts are created by invite
    // (consultants-invite / client-users-admin) or by SSO provisioning, all of which use the admin
    // API — none of them come through here. ConsultantEditForm already passed this flag.
    const { error: otpError } = await supabase.auth.signInWithOtp({
      email,
      options: { shouldCreateUser: false, emailRedirectTo: `${window.location.origin}/operacoes` },
    })
    setSubmitting(false)
    if (otpError) {
      // Supabase reports the no-such-user case as a generic signup-disabled error; say the useful
      // thing instead of leaking the raw string, which reads as an outage to the person typing.
      const unknown =
        /signup|not found|no user|disabled/i.test(otpError.message) ||
        (otpError as { status?: number }).status === 422
      setError(unknown ? pt.auth.unknownEmail : otpError.message)
      return
    }
    setMagicLinkSentTo(email)
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-canvas p-8">
      <div className="w-full max-w-sm space-y-5 rounded-lg border border-DEFAULT bg-surface p-8 shadow-card">
        <div>
          <h1 className="text-[18px] font-semibold text-gray-900">{pt.app.title}</h1>
          <p className="mt-1 text-[12px] text-gray-500">{pt.auth.signInSubtitle}</p>
        </div>

        {magicLinkSentTo ? (
          <div className="space-y-4">
            <p
              className="rounded-md border border-DEFAULT bg-surface-muted p-3 text-[12.5px] text-gray-700"
              role="status"
            >
              {pt.auth.magicLinkSent.replace('{email}', magicLinkSentTo)}
            </p>
            <button
              type="button"
              onClick={() => {
                setMagicLinkSentTo(null)
                setError(null)
              }}
              className="text-[12px] text-gray-600 hover:text-gray-900"
            >
              {pt.auth.backToSignIn}
            </button>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-5">
            <p className="text-[12.5px] text-gray-600">{pt.auth.forgotPasswordIntro}</p>
            <FormField id="email" label={pt.auth.email} required>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </FormField>
            {error && (
              <p className="text-[11.5px] text-red-700" role="alert">
                {error}
              </p>
            )}
            <Button type="submit" disabled={submitting} className="h-9 w-full text-[13px]">
              {pt.auth.sendMagicLink}
            </Button>
          </form>
        )}
      </div>
    </main>
  )
}
